import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  Image,
  Button,
} from "@chakra-ui/react";
import CloseIconBlack from "../../../assets/images/product/closeIconBlack.svg";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import trashIcon from "../../../assets/images/projectManagement/trash-2.svg";
import iconPlus from "../../../assets/images/projectManagement/plus.svg";
import { useCategories } from "../../../categorys/customer/share";
import { FormBonusEmployee } from "../../../components/form/formBonusEmployee";
import useDebounce from "../../../hooks/useDebounce";

const baseURL = process.env.REACT_APP_BACKEND_API || "http://localhost:3001";

const schema = Yup.object().shape({
  customer_shares: Yup.array()
    .min(1, "Vui lòng chọn ít nhất 1 nhân viên")
    .test(
      "not-empty",
      "Vui lòng chọn nhân viên",
      (arr) => Array.isArray(arr) && arr.every((val) => !!val._id)
    )
    .required("Vui lòng chọn ít nhất một nhân viên"),
});

const FormShare = ({
  isOpen,
  onClose,
  defaultValues,
  onSubmit,
  isEdit = false,
}) => {
  const {
    handleSubmit,
    setValue,
    setError,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues || { customer_shares: [] },
  });

  const [employeeProject, setEmployeeProject] = useState([
    { id: Date.now(), employeeId: "", fullName: "", code: "" },
  ]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [focusStates, setFocusStates] = useState({});
  const [searchTermCustomerType, setSearchTermCustomerType] = useState("");
  const debouncedSearchTermCustomerType = useDebounce(
    searchTermCustomerType,
    500
  );

  const customerSharesError = errors.customer_shares;

  useEffect(() => {
    reset(defaultValues);

    // lấy ra nhân viên
    if (defaultValues?.customer_shares?.length > 0) {
      const dataManagerEmployee = (defaultValues?.customer_shares || []).map(
        (empId, index) => ({
          id: Date.now() + index,
          employeeId: empId?._id || "",
          fullName: empId?.fullName || "",
          code: empId?.code || "",
        })
      );

      setEmployeeProject(dataManagerEmployee);
    }
  }, []);

  useEffect(() => {
    const data =
      employeeProject
        // ?.filter((item) => item.employeeId)
        .map((item) => ({
          _id: item.employeeId,
          code: item.code,
          fullName: item.fullName,
        })) || [];

    setValue("customer_shares", data, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [employeeProject, setValue]);

  //Xử lý quản lý nhân viên dự án
  const addEmployee = () => {
    clearErrors("customer_shares");

    setEmployeeProject([
      ...employeeProject,
      { id: Date.now(), employeeId: "" },
    ]);
  };

  const deleteEmployee = async (id) => {
    setEmployeeProject(employeeProject.filter((emp) => emp.id !== id));
  };

  // const handleChange = (id, field, value) => {
  //   setEmployeeProject(
  //     employeeProject.map((emp) =>
  //       emp.id === id ? { ...emp, [field]: value } : emp
  //     )
  //   );
  // };
  const handleChange = (id, field, value) => {
    if (field === "employeeId") {
      const isDuplicate = employeeProject.some(
        (emp) => emp.id !== id && emp.employeeId === value?.value
      );

      if (isDuplicate) {
        setError("customer_shares", {
          type: "manual",
          message: "Nhân viên này đã tồn tại",
        });
        return;
      } else {
        clearErrors("customer_shares");
      }
    }

    setEmployeeProject((prev) =>
      prev.map((emp) =>
        emp.id === id
          ? {
              ...emp,
              [field]: value?.value,
              fullName: value?.label || "",
              code: value?.code || "",
            }
          : emp
      )
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldFetch(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);
  const { constantData } = useCategories(
    shouldFetch,
    debouncedSearchTermCustomerType
  );

  const formatEntries = (entries) =>
    Object.entries(entries || {}).map(([key, value]) => ({
      value: key,
      label: value.fullName,
      code: value.code,
    }));

  const employees = useMemo(
    () => formatEntries(constantData?.employee),
    [constantData?.employee]
  );

  const handleFocus = (field, index) => {
    if (index !== null) {
      setFocusedIndex(index);
    } else {
      setFocusStates((prev) => ({ ...prev, [field]: true }));
    }
  };

  const handleBlur = (field, index) => {
    // setZIndex("");
    if (index !== null) {
      setFocusedIndex(index);
    } else {
      setFocusedIndex(null);
    }
    setFocusStates((prev) => ({ ...prev, [field]: false }));
  };

  const submitHandler = (data) => {
    onSubmit(data);
    // console.log(data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        backgroundColor="var(--color-main)"
        borderRadius={"16px"}
        w={"100%"}
        maxW={"625px"}
        overflowY={"auto"}
        sx={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            width: "4px",
            borderRadius: "50px",
            background: "var(--color-background)",
          },
          "&::-moz-scrollbar": {
            width: "4px",
          },
          "&::-moz-scrollbar-thumb": {
            width: "4px",
            borderRadius: "50px",
            background: "var(--color-background)",
          },
        }}
      >
        <form onSubmit={handleSubmit(submitHandler)}>
          <Flex minH={"625px"} flexDirection={"column"}>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"10px"}
              padding={"25px 24px"}
              borderBottom={"1px solid var(--bg-line-employee)"}
            >
              <Text
                color="var(--color-header-employee)"
                fontSize={"18px"}
                fontWeight={"600"}
              >
                Chia sẻ khách hàng
              </Text>
              <Box cursor={"pointer"} onClick={onClose}>
                <Image src={CloseIconBlack} />
              </Box>
            </Flex>
            <Box flex={1} w={"545px"} m={"24px auto"}>
              {employeeProject.map((emp, index) => {
                return (
                  <FormBonusEmployee
                    key={index}
                    zIndex={index}
                    isIconPlace={true}
                    isIconTime={false}
                    length={"100%"}
                    onChangeName={(e) => {
                      handleChange(emp.id, "employeeId", e);
                    }}
                    deleteData={() => deleteEmployee(emp.id)}
                    icon={trashIcon}
                    selectedMain={emp}
                    filteredOptions={employees}
                    searchTermCustomerType={searchTermCustomerType}
                    setSearchTermCustomerType={setSearchTermCustomerType}
                    isFocused={focusedIndex === index}
                    onFocus={() => handleFocus("managerProject", index)}
                    onBlur={() => {
                      handleBlur("managerProject", index);
                      setSearchTermCustomerType("");
                    }}
                    required={true}
                  />
                );
              })}

              <Box>
                {customerSharesError &&
                  typeof customerSharesError !== "string" && (
                    <Text fontSize="12px" color="red" lineHeight={"15px"}>
                      {customerSharesError?.message || ""}
                    </Text>
                  )}
              </Box>

              <Flex
                gap={"4px"}
                alignItems={"end"}
                p="8px 16px"
                border={"1px solid var(--color-option-employee-hover)"}
                borderRadius={"8px"}
                w={"max-content"}
                mr={"60px"}
                mt={"24px"}
                cursor={"pointer"}
                bg={"var(--color-main)"}
                onClick={addEmployee}
                _hover={{
                  filter: "brightness(90%)",
                }}
              >
                <Image src={iconPlus} />
                <Text
                  fontSize={"12px"}
                  lineHeight={"15px"}
                  color={"var(--color-option-employee-hover)"}
                  fontWeight={600}
                >
                  Thêm NV chia sẻ
                </Text>
              </Flex>
            </Box>
            <Flex p={"24px"} gap={"16px"} justifyContent={"flex-end"}>
              {isEdit ? (
                <>
                  <Button
                    onClick={onClose}
                    w={"112px"}
                    h={"44px"}
                    color={"var(--color-option-employee-hover)"}
                    border={"1px solid var(--color-option-employee-hover)"}
                    bg={"var(--color-main)"}
                    _hover={{
                      filter: "brightness(80%)",
                    }}
                  >
                    Hủy
                  </Button>
                  <Button
                    type="submit"
                    w={"112px"}
                    h={"44px"}
                    color={"var(--color-main)"}
                    bg={"var(--color-button)"}
                    _hover={{
                      filter: "brightness(80%)",
                    }}
                  >
                    Lưu
                  </Button>
                </>
              ) : (
                <Button
                  type="submit"
                  w={"164px"}
                  h={"44px"}
                  color={"var(--color-main)"}
                  bg={"var(--color-button)"}
                  _hover={{
                    filter: "brightness(80%)",
                  }}
                >
                  Tạo khách hàng
                </Button>
              )}
            </Flex>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default FormShare;
